import type { RichTextEmojiInsert } from "@library-elements/text";
import type { MutableRefObject } from "react";
import {
  pillDisplayText,
  VARIABLE_PILL_ATTR,
  VARIABLE_PILL_CLASS,
} from "@/shared/utils/variable-pill";
import {
  escapeAfterPill,
  PILL_CARET_GUARD,
} from "@/shared/utils/pill-caret-guards";
import { pillAtPoint } from "@/shared/utils/pill-atomic";
import { rangeAtRootEnd } from "@/shared/utils/caret-from-point";
import { restoreLastRange } from "../selection-manager";
import type { FroalaEditorInstance } from "../types";

export const handleInsertEmoji = (
  editor: FroalaEditorInstance,
  data: RichTextEmojiInsert,
  lastRange: MutableRefObject<Range | null>,
  refresh: () => void,
) => {
  const sel = window.getSelection();
  if (!sel) return;

  const range = restoreLastRange(lastRange, sel);
  if (!range) return;

  const emojiEl = document.createElement("em-emoji");
  emojiEl.contentEditable = "false";
  emojiEl.setAttribute("shortcodes", data.emoji.shortcodes);
  range.insertNode(emojiEl);

  const newRange = new Range();
  newRange.setStartAfter(emojiEl);
  newRange.setEndAfter(emojiEl);
  sel.removeAllRanges();
  sel.addRange(newRange);

  editor.save.save();
  refresh();
};

export const handleInsertIcon = (
  editor: FroalaEditorInstance,
  data: { unicode: string; icon: string },
  lastRange: MutableRefObject<Range | null>,
  refresh: () => void,
) => {
  const sel = window.getSelection();
  if (!sel) return;

  const range = restoreLastRange(lastRange, sel);
  if (!range) return;

  const iconEl = document.createElement("i");
  iconEl.contentEditable = "false";

  if (data.icon.startsWith("ri-")) {
    iconEl.setAttribute("data-is-remix-icon", "true");
    iconEl.setAttribute("data-remix-unicode", data.unicode);
  } else if (data.icon.startsWith("fa-")) {
    iconEl.setAttribute("data-is-font-awesome-icon", "true");
    iconEl.setAttribute("data-font-awesome-unicode", data.unicode);
  }

  iconEl.innerHTML = `<span>${String.fromCharCode(Number.parseInt(data.unicode, 16))}</span>`;
  range.insertNode(iconEl);

  const newRange = new Range();
  newRange.setStartAfter(iconEl);
  newRange.setEndAfter(iconEl);
  sel.removeAllRanges();
  sel.addRange(newRange);

  editor.save.save();
  refresh();
};

export const handleDynamicVariable = (
  editor: FroalaEditorInstance,
  data: string,
  lastRange: MutableRefObject<Range | null>,
  refresh: () => void,
) => {
  const sel = window.getSelection();
  if (!sel) return;

  const editable = editor.$el.get(0);
  const range = restoreLastRange(lastRange, sel);
  if (!range) {
    // No saved caret (e.g. insert from the variables panel without prior focus):
    // append at the end of the content instead of dropping the insert.
    editor.selection.setAtEnd(editable);
    editor.selection.restore();
  }

  // Double-brace tokens insert as an atomic pill (same class/attrs the save
  // path strips back to {{name}} — content-manager.ts). Anything else (e.g.
  // {{{blocks}}}, [list] child tokens) keeps the legacy raw-string insert.
  const tokenMatch = /^\{\{([^{}\s]+)\}\}$/.exec(data.trim());
  // Froala's restore above only applies a selection to an editor it can focus;
  // an insert into a blurred, never-typed-in editor (an empty text element the
  // user only clicked once) otherwise has no range at all and would degrade to
  // a raw-token html.insert.
  const liveRange =
    sel.rangeCount > 0
      ? sel.getRangeAt(0)
      : (range ?? rangeAtRootEnd(editable));

  if (tokenMatch && liveRange) {
    const name = tokenMatch[1];
    const pill = document.createElement("span");
    pill.className = VARIABLE_PILL_CLASS;
    pill.setAttribute(VARIABLE_PILL_ATTR, name);
    // Non-editable atom, same as variable-pill.ts's createPillElement; the
    // editor's `allowStylingOnNonEditable` lets formatting wrap it.
    pill.setAttribute("contenteditable", "false");
    pill.textContent = pillDisplayText(name, "");

    // The range may start INSIDE an existing pill — the user clicked a pill
    // (which selects the atom, see Froala.tsx) and then picked a variable, or
    // a restored range collapsed into one. Inserting there nests the new atom
    // inside the old one, and the save path serializes only the outer token,
    // so the variable the user just inserted is silently lost. Snap out to
    // just after the enclosing pill before inserting.
    const enclosingPill = pillAtPoint(liveRange.startContainer);
    if (enclosingPill) {
      const escaped = escapeAfterPill(enclosingPill);
      liveRange.setStart(escaped.node, escaped.offset);
      liveRange.collapse(true);
    }

    liveRange.insertNode(pill);

    // Caret guards: give the caret a landing spot on both sides of the atom
    // (transient — stripZWS removes them on save).
    const doc = pill.ownerDocument;
    if (
      !pill.previousSibling ||
      pill.previousSibling.nodeType !== Node.TEXT_NODE
    ) {
      pill.parentNode?.insertBefore(doc.createTextNode(PILL_CARET_GUARD), pill);
    }
    let after = pill.nextSibling;
    if (!after || after.nodeType !== Node.TEXT_NODE) {
      after = doc.createTextNode(PILL_CARET_GUARD);
      pill.parentNode?.insertBefore(after, pill.nextSibling);
    }

    const newRange = new Range();
    if (
      after.nodeType === Node.TEXT_NODE &&
      after.textContent?.startsWith(PILL_CARET_GUARD)
    ) {
      newRange.setStart(after, 1); // just past the guard — typing continues naturally
    } else {
      newRange.setStartAfter(pill);
    }
    newRange.collapse(true);
    sel.removeAllRanges();
    sel.addRange(newRange);
    // Keep the picker insertion point in sync with the native selection.
    // Without this, opening the picker again before a selectionchange event
    // restores the range from before the previous pill and inserts the next
    // token at the wrong empty-textbox boundary.
    lastRange.current = newRange.cloneRange();
  } else {
    editor.html.insert(data);
  }

  editor.save.save();
  refresh();
};
